import React, { useEffect, useState } from 'react';
import { Award, ClipboardList, DollarSign, FileCheck, FileText, Gavel, Mail, MapPinned, Plus, Search, Upload, X } from 'lucide-react';
import { CUSTOM_DASHBOARD_TEMPLATE, DASHBOARD_TEMPLATES } from '../../helpers/dashboardTemplates';
import { prepareCsvDashboard } from '../../helpers/csvImport';
import './DashboardTemplatePicker.css';

const templateIcons = {
  searching: Search,
  costs: DollarSign,
  tours: MapPinned,
  applications: ClipboardList,
  scholarships: Award,
  essays: FileText,
  contacts: Mail,
  decisions: Gavel,
  'css-fafsa': FileCheck,
};

function DashboardTemplateCard({ template, onSelect }) {
  const Icon = templateIcons[template.id] || Plus;
  const isCustom = template.id === 'custom';

  return (
    <button
      type="button"
      className={`dashboard-template-card ${isCustom ? 'dashboard-template-card--custom' : ''}`}
      onClick={() => onSelect(template.id)}
    >
      <div className="dashboard-template-card__preview" aria-hidden="true">
        <div className="dashboard-template-card__preview-top">
          <span className="dashboard-template-card__icon">
            <Icon size={16} strokeWidth={1.8} />
          </span>
          <span className="dashboard-template-card__badge">
            {isCustom ? 'Blank canvas' : 'Template'}
          </span>
        </div>
        {isCustom ? (
          <div className="dashboard-template-card__custom-plus">
            <Plus size={34} strokeWidth={1.7} />
          </div>
        ) : (
          <>
            <div className="dashboard-template-card__table">
              {template.previewColumns.slice(0, 4).map((column) => (
                <span key={column}>{column}</span>
              ))}
            </div>
            <div className="dashboard-template-card__rows">
              <span />
              <span />
              <span />
            </div>
          </>
        )}
      </div>

      <div className="dashboard-template-card__copy">
        <strong>{template.name}</strong>
        <span>{template.description}</span>
      </div>
    </button>
  );
}

function DashboardTemplatePicker({ finalListCount = 0, onClose, onSelectTemplate, shouldPromptForFinalList }) {
  const templates = [CUSTOM_DASHBOARD_TEMPLATE, ...DASHBOARD_TEMPLATES];
  const [pendingTemplate, setPendingTemplate] = useState(null);
  const [csvImport, setCsvImport] = useState(null);
  const [csvError, setCsvError] = useState('');

  const handleCsvFile = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    setCsvError('');
    try {
      if (!file.name.toLowerCase().endsWith('.csv')) {
        throw new Error('Choose a CSV file exported from Excel, Google Sheets, or Numbers.');
      }
      const importData = prepareCsvDashboard(await file.text());
      setCsvImport({ fileName: file.name, importData });
    } catch (error) {
      setCsvImport(null);
      setCsvError(error.message || 'This CSV could not be read.');
    }
  };

  const handleSelectTemplate = (templateId) => {
    const template = templates.find((item) => item.id === templateId);
    if (template && finalListCount > 0 && shouldPromptForFinalList?.(template.id)) {
      setPendingTemplate(template);
      return;
    }

    onSelectTemplate(templateId, { prefillFinalList: false });
  };

  const handleUseFinalList = () => {
    if (!pendingTemplate) return;
    onSelectTemplate(pendingTemplate.id, { prefillFinalList: true });
  };

  const handleStartEmpty = () => {
    if (!pendingTemplate) return;
    onSelectTemplate(pendingTemplate.id, { prefillFinalList: false });
  };

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') onClose();
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  return (
    <div className="dashboard-template-picker" role="dialog" aria-modal="true" aria-labelledby="dashboard-template-picker-title">
      <div className="dashboard-template-picker__backdrop" onClick={onClose} />
      <div className="dashboard-template-picker__panel">
        <div className="dashboard-template-picker__header">
          <div>
            <h2 id="dashboard-template-picker-title">Choose a dashboard</h2>
            <p>Start with a setup that matches what you are tracking.</p>
          </div>
          <button type="button" className="dashboard-template-picker__close" onClick={onClose} aria-label="Close">
            <X size={17} strokeWidth={1.8} />
          </button>
        </div>

        {csvImport ? (
          <div className="dashboard-template-picker__csv-review">
            <div className="dashboard-template-picker__csv-summary">
              <span className="dashboard-template-card__icon"><Upload size={16} strokeWidth={1.8} /></span>
              <div>
                <strong>Review {csvImport.fileName}</strong>
                <p>{csvImport.importData.preview.rowCount} rows ready to import</p>
              </div>
            </div>
            <div className="dashboard-template-picker__csv-matches">
              <div>
                <strong>Matched MaiColleges columns</strong>
                <p>{csvImport.importData.preview.matchedHeaders.join(', ') || 'None'}</p>
              </div>
              <div>
                <strong>New Custom Fields</strong>
                <p>{csvImport.importData.preview.customHeaders.join(', ') || 'None'}</p>
              </div>
            </div>
            <div className="dashboard-template-picker__csv-table-wrap">
              <table>
                <thead><tr>{csvImport.importData.preview.headers.map((header, index) => <th key={`${header}-${index}`}>{header}</th>)}</tr></thead>
                <tbody>
                  {csvImport.importData.preview.rows.map((row, rowIndex) => (
                    <tr key={rowIndex}>{csvImport.importData.preview.headers.map((header, columnIndex) => <td key={`${header}-${columnIndex}`}>{row[columnIndex] || ''}</td>)}</tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="dashboard-template-picker__csv-note">Capitalization, extra spaces, hyphens, and underscores are ignored when matching column names. You can add or remove columns later.</p>
            <div className="dashboard-template-picker__prefill-actions">
              <button type="button" onClick={() => onSelectTemplate('custom', { importData: csvImport.importData })}>Create Imported Dashboard</button>
              <button type="button" onClick={() => setCsvImport(null)}>Back</button>
            </div>
          </div>
        ) : pendingTemplate ? (
          <div className="dashboard-template-picker__prefill dashboard-template-picker__prefill--focused" role="alert">
            <div>
              <strong>Use your final college list to start this dashboard?</strong>
              <p>{pendingTemplate.name} can be created with your {finalListCount} final {finalListCount === 1 ? 'college' : 'colleges'} already added.</p>
            </div>
            <div className="dashboard-template-picker__prefill-actions">
              <button type="button" onClick={handleUseFinalList}>Use Final List</button>
              <button type="button" onClick={handleStartEmpty}>Start Empty</button>
              <button type="button" onClick={() => setPendingTemplate(null)}>Choose Different Dashboard</button>
            </div>
          </div>
        ) : (
          <div className="dashboard-template-picker__grid">
            <DashboardTemplateCard template={CUSTOM_DASHBOARD_TEMPLATE} onSelect={handleSelectTemplate} />
            <label className="dashboard-template-card dashboard-template-card--custom dashboard-template-card--import">
              <input type="file" accept=".csv,text/csv" onChange={handleCsvFile} />
              <div className="dashboard-template-card__preview" aria-hidden="true">
                <div className="dashboard-template-card__preview-top">
                  <span className="dashboard-template-card__icon"><Upload size={16} strokeWidth={1.8} /></span>
                  <span className="dashboard-template-card__badge">CSV file</span>
                </div>
                <div className="dashboard-template-card__custom-plus"><Upload size={32} strokeWidth={1.6} /></div>
              </div>
              <div className="dashboard-template-card__copy">
                <strong>Import CSV</strong>
                <span>Start from an Excel, Google Sheets, or Numbers export.</span>
              </div>
            </label>
            {DASHBOARD_TEMPLATES.map((template) => <DashboardTemplateCard key={template.id} template={template} onSelect={handleSelectTemplate} />)}
            {csvError && <p className="dashboard-template-picker__csv-error" role="alert">{csvError}</p>}
          </div>
        )}
      </div>
    </div>
  );
}

export default DashboardTemplatePicker;
